import { useState } from 'react';
import { DATE_TIME_FORMAT, HEALTH_STATES } from '@/const';
import { format } from 'date-fns';
import { NotificationAlert } from '@/data/notifications';
import {
  useClearNotificationsMutation,
  useLazyGetNotificationsQuery,
  useReadNotificationsMutation,
} from '@/store/api/notificationApi';
import NotificationModal from '@/components/shared/feedback/NotificationModal';
import { TrashIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';
import { colors, HealthSeverity } from '@/constants/colors';
import { Button } from '@/components/ui/button';

export const AlertRow = ({ notification }: { notification: NotificationAlert }) => {
  const [isOpen, setIsOpen] = useState(false);
  const state = HEALTH_STATES[notification.severity];
  const IconComponent = state?.icon;

  const [clearNotificationsApi] = useClearNotificationsMutation();
  const [readNotifications] = useReadNotificationsMutation();
  const [fetchNotifications] = useLazyGetNotificationsQuery();

  const [isRemoveClicked, setRemoveClicked] = useState(false);
  const [isDialogOpened, setDialogOpened] = useState<boolean>(false);

  const onNotificationClear = async () => {
    const id = notification.notification_id;

    await clearNotificationsApi([id]);
    setDialogOpened(false);

    await fetchNotifications({});
    setRemoveClicked(false);
  };

  const onNotificationClick = async () => {
    setIsOpen(true);
    await readNotifications([notification.notification_id]);
    await fetchNotifications({});
  };

  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDialogOpened(false);
  };

  const onDeleteSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setRemoveClicked(true);
    await onNotificationClear();
  };

  const onClearClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDialogOpened(true);
  };

  return (
    <>
      <div
        className={`transition-all duration-200 ${isRemoveClicked ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}
      >
        <div
          className={`px-6 group hover:cursor-pointer hover:bg-[#f7f7f7] ${!notification.read ? 'bg-[rgba(112,113,243,0.06)]' : ''}`}
          onClick={onNotificationClick}
        >
          {isDialogOpened ? (
            <div className="px-0 py-3 text-center">
              <div className="text-sm pb-[0.4375rem]">Are you sure you want to delete?</div>
              <div className="flex items-center justify-center gap-2">
                <Button size="sm" onClick={onCancel}>
                  Cancel
                </Button>
                <Button size="sm" variant="destructive" onClick={onDeleteSubmit}>
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div>
                  <div className="flex justify-center items-center w-10 h-10 rounded-full bg-gray-100">
                    <IconComponent
                      className={`text-[${colors.health[notification.severity as HealthSeverity]}] w-6 h-6`}
                    />
                  </div>
                </div>
                <div className="text-xs py-5">
                  <div className="font-semibold">
                    {notification.asset_ui_name}
                    {notification.component_name ? ` / ${notification.component_name}` : null} –{' '}
                    {notification.event_ui_name}
                  </div>
                  <div>
                    {notification.site_name} {format(new Date(notification.created_at), DATE_TIME_FORMAT)}
                  </div>
                </div>
              </div>
              <div className="invisible group-hover:visible">
                <Button variant="outline" size="sm" className="text-red-600 border-red-600" onClick={onClearClick}>
                  <Icon icon={TrashIcon} />
                </Button>
              </div>
            </div>
          )}
        </div>
        <hr className="border-t border-gray-200" />
      </div>
      <NotificationModal isOpen={isOpen} onClose={() => setIsOpen(false)} notification={notification} />
    </>
  );
};
